import React from 'react';
import {useMembershipPlans} from '../hooks/useMembershipPlans';
import {useNavigate} from 'react-router-dom';
import SectionHeader from '@/shared/components/SectionHeader';
import mylabsPlusLogo from '@/assets/dashboard/mylabs_plus_logo.png';

interface MyLabsPlusCardProps {
    isSubscribed?: boolean;
}

/**
 * Compact My Labs Plus upsell card from `mld-dash-26-v10.psd`.
 *
 * At the source artboard's 1920px desktop width the card is 505 x 300px. Its
 * background is a single diagonal gradient from the warm page neutral into
 * Plus mint, with a 1px mint border and 38px corners. Keep the measured logo,
 * price and CTA sizes below intact so the fluid sidebar scales without losing
 * the desktop composition.
 */
const MyLabsPlusCard: React.FC<MyLabsPlusCardProps> = ({isSubscribed = false}) => {
    const membershipPlans = useMembershipPlans();
    const monthly = membershipPlans.find((p) => p.id === 'monthly') ?? membershipPlans[0];
    const navigate = useNavigate();
    if (isSubscribed) return null;

    return (
        <section className="space-y-3">
            <SectionHeader title="My Labs Plus" className="!font-normal" />
            <div className="flex min-h-[300px] flex-col overflow-hidden rounded-2xl border border-[#c2efe2] bg-gradient-to-bl from-[#eef1eb] to-mld-plus-mint px-9 pb-[42px] pt-[34px] text-mld-plus-ink tracking-[-0.02em] sm:px-[42px]">
                <img src={mylabsPlusLogo} alt="My Labs Plus" className="mx-auto h-auto w-[212px] max-w-[65%]" />

                <p className="mx-auto mt-5 max-w-[372px] text-center text-[17px] font-normal leading-[1.38]">
                    Exclusive member deals, customized
                    <br className="hidden sm:block" />
                    follow-up testing, free shipping and more
                </p>

                <div className="mt-auto flex flex-col items-center justify-between gap-5 sm:flex-row sm:gap-6">
                    <div className="shrink-0 text-center">
                        <p className="text-[24px] font-normal leading-[29px]">{`$${(monthly.priceCents / 100).toFixed(2)}/mo`}</p>
                        <p className="mt-0.5 text-[11px] font-normal leading-[14px] text-mld-plus-ink">
                            Cancel Anytime
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate('/membership/benefits')}
                        className="flex h-[51px] w-[260px] max-w-full shrink-0 items-center justify-center gap-[17px] rounded-full bg-[#5bcfde] px-6 text-[16px] font-medium leading-5 text-white transition hover:cursor-pointer hover:brightness-[0.97] focus:outline-none focus:ring-2 focus:ring-mld-plus-ink/20">
                        <span>Explore Benefits</span>
                        <svg
                            viewBox="0 0 10 17"
                            fill="none"
                            className="h-[17px] w-[10px]"
                            stroke="currentColor"
                            strokeWidth="1.5">
                            <path d="M1 1l8 7.5L1 16" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
            </div>
        </section>
    );
};

export default MyLabsPlusCard;
